import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Field, TextInput, TextArea, ImageInput, SaveBar } from "@/admin/ui";

const PAGE_LABELS = {
  home: "Home Page",
  about: "About Page",
  samples: "Samples Page",
  testimonials: "Testimonials Page",
  faq: "FAQ Page",
  contact: "Contact Page",
  privacy: "Privacy Policy",
  terms: "Terms of Service",
  "service-website-development": "Service — Website Development",
  "service-ad-setup": "Service — Ad Setup",
  "service-lead-generation": "Service — Lead Generation",
  "service-ugc-videos": "Service — UGC Videos",
};

export default function SEOManager() {
  const { settings, reload } = useSite();
  const [draft, setDraft] = useState(null);
  const [page, setPage] = useState("home");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings?.seo && !draft) setDraft(JSON.parse(JSON.stringify(settings.seo)));
  }, [settings]);

  if (!draft) {
    return <div className="py-24 flex justify-center"><Loader2 className="h-7 w-7 text-accent-bright animate-spin" /></div>;
  }

  const p = draft.pages?.[page] || {};
  const setPageField = (key, value) =>
    setDraft((d) => ({ ...d, pages: { ...d.pages, [page]: { ...(d.pages?.[page] || {}), [key]: value } } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { seo: draft });
      await reload();
      toast.success("SEO settings saved");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-seo-page">
      <h1 className="font-display text-xl sm:text-2xl font-bold text-white tracking-tight mb-2">SEO Manager</h1>
      <p className="text-xs text-subtle mb-8">Per-page meta tags, default social share image and robots.txt.</p>

      <div className="glass rounded-2xl p-6 sm:p-8 mb-6">
        <h2 className="font-display text-base font-semibold text-white mb-5">Global</h2>
        <Field label="Default OG Image" hint="Used when a page has no image of its own">
          <ImageInput value={draft.default_og} onChange={(v) => setDraft((d) => ({ ...d, default_og: v }))} testid="seo-default-og" />
        </Field>
        <Field label="robots.txt">
          <TextArea value={draft.robots_txt} onChange={(e) => setDraft((d) => ({ ...d, robots_txt: e.target.value }))} rows={5} mono testid="seo-robots-input" />
        </Field>
        <p className="text-[11px] text-subtle -mt-2">Served live at <span className="font-mono text-accent-bright">/api/robots.txt</span></p>
      </div>

      <div className="glass rounded-2xl p-6 sm:p-8">
        <h2 className="font-display text-base font-semibold text-white mb-5">Page Meta</h2>
        <div className="flex flex-wrap gap-2 mb-6">
          {Object.entries(PAGE_LABELS).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setPage(key)}
              className={`px-3.5 py-2 rounded-full text-[11px] font-semibold border transition-all duration-300 ${page === key ? "bg-accent border-accent text-white" : "border-white/15 text-mist hover:text-white hover:border-accent/50"}`}
              data-testid={`seo-page-tab-${key}`}
            >
              {label}
            </button>
          ))}
        </div>
        <Field label="Meta Title" hint={`${(p.title || "").length}/60 characters`}>
          <TextInput value={p.title} onChange={(e) => setPageField("title", e.target.value)} testid="seo-page-title" />
        </Field>
        <Field label="Meta Description" hint={`${(p.description || "").length}/160 characters`}>
          <TextArea value={p.description} onChange={(e) => setPageField("description", e.target.value)} rows={3} testid="seo-page-description" />
        </Field>
        <Field label="Focus Keywords" hint="Comma separated">
          <TextInput value={p.keywords} onChange={(e) => setPageField("keywords", e.target.value)} testid="seo-page-keywords" />
        </Field>
        <Field label="Canonical URL" hint="Leave empty for default">
          <TextInput value={p.canonical} onChange={(e) => setPageField("canonical", e.target.value)} testid="seo-page-canonical" />
        </Field>
        <Field label="OG / Twitter Share Image">
          <ImageInput value={p.og_image} onChange={(v) => setPageField("og_image", v)} testid="seo-page-og" />
        </Field>
        <SaveBar onSave={save} saving={saving} testid="seo-save-btn" />
      </div>
    </div>
  );
}
